import { useEffect, useRef, useState, useCallback } from 'react';
import websocketService, { WebSocketMessage, WebSocketEventHandler } from '../services/websocketService';

interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  autoConnect?: boolean;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  connectionState: string;
  lastMessage: WebSocketMessage | null;
  sendMessage: (message: Omit<WebSocketMessage, 'timestamp'>) => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  addEventListener: (eventType: string, handler: WebSocketEventHandler) => void;
  removeEventListener: (eventType: string, handler: WebSocketEventHandler) => void;
}

export const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  const {
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    autoConnect = true
  } = options;

  // Check if WebSocket is enabled via environment variable
  const isWebSocketEnabled = process.env.REACT_APP_ENABLE_WEBSOCKET === 'true';

  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('DISCONNECTED');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  
  const handlersRef = useRef<Map<string, WebSocketEventHandler>>(new Map());

  // Track previous connection state to avoid duplicate callbacks
  const prevConnectedRef = useRef<boolean>(false);

  // Update connection state
  const updateConnectionState = useCallback(() => {
    const connected = websocketService.isConnected();
    const state = websocketService.getConnectionState();

    setIsConnected(connected);
    setConnectionState(state);

    // Only call callbacks when connection state actually changes
    if (connected !== prevConnectedRef.current) {
      if (connected && onConnect) {
        onConnect();
      } else if (!connected && onDisconnect) {
        onDisconnect();
      }
      prevConnectedRef.current = connected;
    }
  }, [onConnect, onDisconnect]);

  // Handle incoming messages
  const handleMessage = useCallback((message: WebSocketMessage) => {
    setLastMessage(message);
    if (onMessage) {
      onMessage(message);
    }
  }, [onMessage]);

  // Send message with timestamp
  const sendMessage = useCallback((message: Omit<WebSocketMessage, 'timestamp'>) => {
    const fullMessage: WebSocketMessage = {
      ...message,
      timestamp: new Date().toISOString()
    };
    websocketService.send(fullMessage);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    if (!isWebSocketEnabled) {
      console.log('WebSocket is disabled via environment variable');
      return;
    }

    try {
      await websocketService.connect();
      updateConnectionState();
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      if (onError) {
        onError(error as Event);
      }
    }
  }, [isWebSocketEnabled, updateConnectionState, onError]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    websocketService.disconnect();
    updateConnectionState();
  }, [updateConnectionState]);

  // Add event listener
  const addEventListener = useCallback((eventType: string, handler: WebSocketEventHandler) => {
    websocketService.addEventListener(eventType, handler);
    handlersRef.current.set(`${eventType}_${Date.now()}`, handler);
  }, []);

  // Remove event listener
  const removeEventListener = useCallback((eventType: string, handler: WebSocketEventHandler) => {
    websocketService.removeEventListener(eventType, handler);
  }, []);

  // Setup effect
  useEffect(() => {
    // Add global message handler
    const globalHandler = handleMessage;
    websocketService.addEventListener('*', globalHandler);

    // Auto-connect if enabled and WebSocket is enabled via environment variable
    if (autoConnect && isWebSocketEnabled) {
      connect();
    }

    // Cleanup function
    return () => {
      websocketService.removeEventListener('*', globalHandler);

      // Remove all handlers added by this hook
      // Copy the current handlers to avoid stale closure issues
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const currentHandlers = handlersRef.current;
      currentHandlers.forEach((handler, key) => {
        const eventType = key.split('_')[0];
        websocketService.removeEventListener(eventType, handler);
      });
      currentHandlers.clear();
    };
  }, [autoConnect, isWebSocketEnabled, connect, handleMessage]);

  // Update connection state periodically (less frequently to avoid spam)
  useEffect(() => {
    const interval = setInterval(updateConnectionState, 5000); // Check every 5 seconds instead of 1
    return () => clearInterval(interval);
  }, [updateConnectionState]);

  return {
    isConnected,
    connectionState,
    lastMessage,
    sendMessage,
    connect,
    disconnect,
    addEventListener,
    removeEventListener
  };
};

export default useWebSocket;
